import { createSelector } from 'reselect';
import { denormalize } from 'normalizr';

import * as schemas from 'store/common/schemas';
import {
  selectAllEntities,
  selectCompanyAdvisorConnectionsEntities,
} from 'store/entities/selectors';
import * as helpers from 'store/features/company-advisor-connections/helpers';

const selectUsersAdvisoriesFeatureState = state => state.features.users.advisories;

const selectUsersAdvisoriesFeatureStateById = createSelector(
  selectUsersAdvisoriesFeatureState,
  featureState => featureState.byId
);

const selectUsersAdvisoriesFeatureStateByUserId = createSelector(
  selectUsersAdvisoriesFeatureState,
  featureState => featureState.byUserId
);

export const selectUserAdvisories = createSelector(
  selectCompanyAdvisorConnectionsEntities,
  (state, props) => props.userId,
  selectAllEntities,
  (advisorConnectionsById, userId, entities) =>
    denormalize(Object.keys(advisorConnectionsById), [schemas.companyAdvisorConnection], entities)
      .filter(ac => ac && ac.user && ac.user.id === userId)
      .map(ac => ({
        ...ac,
        type: helpers.calculateAdvisorConnectionType(ac),
        status: helpers.calculateAdvisorConnectionStatus({ ...ac }),
        connectionType: helpers.calculateAdvisorConnectionType({ ...ac }),
      }))
);

export const selectAdvisory = createSelector(
  (state, props) => props.advisoryId,
  selectAllEntities,
  (advisoryId, entities) => {
    const advisory = denormalize(advisoryId, schemas.companyAdvisorConnection, entities);
    if (advisory) {
      advisory.type = helpers.calculateAdvisorConnectionType(advisory);
      advisory.status = helpers.calculateAdvisorConnectionStatus(advisory);
      advisory.connectionType = helpers.calculateAdvisorConnectionType(advisory);
    }
    return advisory;
  }
);

export const selectIsFetchingUserAdvisories = createSelector(
  selectUsersAdvisoriesFeatureStateByUserId,
  (state, props) => props.userId,
  (advisoriesFeatureByUserId, userId) =>
    advisoriesFeatureByUserId[userId] && advisoriesFeatureByUserId[userId].fetching
);

export const selectIsFetchedUserAdvisoriesAtleastOnce = createSelector(
  selectUsersAdvisoriesFeatureStateByUserId,
  (state, props) => props.userId,
  (advisoriesFeatureByUserId, userId) =>
    advisoriesFeatureByUserId[userId] && advisoriesFeatureByUserId[userId].lastFetchedAt
);

export const selectIsFetchingAdvisory = createSelector(
  selectUsersAdvisoriesFeatureStateById,
  (state, props) => props.advisoryId,
  (advisoriesFeatureById, advisoryId) =>
    advisoriesFeatureById[advisoryId] && advisoriesFeatureById[advisoryId].fetching
);

export const selectIsFetchedAdvisoryAtleastOnce = createSelector(
  selectUsersAdvisoriesFeatureStateById,
  (state, props) => props.advisoryId,
  (advisoriesFeatureById, advisoryId) =>
    advisoriesFeatureById[advisoryId] && advisoriesFeatureById[advisoryId].lastFetchedAt
);

export const selectIsCheckingAcceptAdvisoryStatus = createSelector(
  selectUsersAdvisoriesFeatureStateById,
  (state, props) => props.advisoryId,
  (advisoriesFeatureById, advisoryId) =>
    advisoriesFeatureById[advisoryId] &&
    advisoriesFeatureById[advisoryId].checkingAcceptAdvisoryStatus
);

export const selectIsCheckedAcceptAdvisoryStatusAtleastOnce = createSelector(
  selectUsersAdvisoriesFeatureStateById,
  (state, props) => props.advisoryId,
  (advisoriesFeatureById, advisoryId) =>
    advisoriesFeatureById[advisoryId] &&
    !!advisoriesFeatureById[advisoryId].lastCheckedAcceptAdvisoryStatusAt
);

export const selectIsAcceptingAdvisory = createSelector(
  selectUsersAdvisoriesFeatureStateById,
  (state, props) => props.advisoryId,
  (advisoriesFeatureById, advisoryId) =>
    advisoriesFeatureById[advisoryId] && advisoriesFeatureById[advisoryId].accepting
);

export const selectIsRejectingAdvisory = createSelector(
  selectUsersAdvisoriesFeatureStateById,
  (state, props) => props.advisoryId,
  (advisoriesFeatureById, advisoryId) =>
    advisoriesFeatureById[advisoryId] && advisoriesFeatureById[advisoryId].rejecting
);

export const selectIsRevokingAdvisory = createSelector(
  selectUsersAdvisoriesFeatureStateById,
  (state, props) => props.advisoryId,
  (advisoriesFeatureById, advisoryId) =>
    advisoriesFeatureById[advisoryId] && advisoriesFeatureById[advisoryId].revoking
);

export const selectIsPollingAnyAdvisory = createSelector(
  selectCompanyAdvisorConnectionsEntities,
  companyAdvisorEntities => {
    const ids = Object.keys(companyAdvisorEntities);
    for (let i = 0; i < ids.length; i++) {
      if (companyAdvisorEntities[ids[i]] && companyAdvisorEntities[ids[i]].polling) return true;
    }
    return false;
  }
);

export const isValidatingSoftConnection = createSelector(
  selectUsersAdvisoriesFeatureStateById,
  (state, props) => props.advisoryId,
  (advisoriesFeatureById, advisoryId) =>
    advisoriesFeatureById[advisoryId] &&
    advisoriesFeatureById[advisoryId].softConnection &&
    advisoriesFeatureById[advisoryId].softConnection.validating
);

export const isAcceptingSoftConnection = createSelector(
  selectUsersAdvisoriesFeatureStateById,
  (state, props) => props.advisoryId,
  (advisoriesFeatureById, advisoryId) =>
    advisoriesFeatureById[advisoryId] &&
    advisoriesFeatureById[advisoryId].softConnection &&
    advisoriesFeatureById[advisoryId].softConnection.accepting
);

export const isRejectingSoftConnection = createSelector(
  selectUsersAdvisoriesFeatureStateById,
  (state, props) => props.advisoryId,
  (advisoriesFeatureById, advisoryId) =>
    advisoriesFeatureById[advisoryId] &&
    advisoriesFeatureById[advisoryId].softConnection &&
    advisoriesFeatureById[advisoryId].softConnection.rejecting
);



// WEBPACK FOOTER //
// ./src/store/features/users/advisories/selectors.js